<template>
  <div class="gauge-container">
    <div :ref="entityId" class="gauge" autoresize></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'VueGauge',
  props: {
    entityId: { type: String, default: Math.floor(100000000 + Math.random() * 900000000).toString() },
    value: { type: Number, required: true },
    unit: { type: String, default: 'u' },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    sectors: { type: Array, default: () => [20, 40, 60, 80] }
  },
  data() {
    return {
      chart: null,
      colors: ["#8f060f", "#ff9900", "#05b80b", "#ff9900", "#8f060f"]
    };
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  watch: {
    value(val) {
      if (this.chart) {
        this.chart.setOption({
          series: [
            {
              data: [{ value: val, name: 'Presión' }],
              pointer: { show: false },
              progress: {
                show: true,
                overlap: false,
                roundCap: true,
                clip: false,
                itemStyle: { 
                  borderWidth: 12,
                  borderColor: this.getColorForValue(val)
                }
            },
            }
          ]
        });
      }
    },
    min() { this.initChart(true); },
    max() { this.initChart(true); },
    unit() { this.initChart(true); }
  },
  methods: {
    handleResize() {
      if (this.chart) this.chart.resize();
    },
    buildOption() {
      const min = this.min;
      const max = this.max;
      const value = this.value;
      const sectors = this.sectors;
      const colors = this.colors;

      // Build color mapping for gauge axis
      const colorsBySectors = sectors.map((s, i) => [(s - min) / (max - min), colors[i]]);

      // Determine progress color based on current value
      const progressColor = this.getColorForValue(value);

      // Formatter for axis labels
      function valuesBySectors(v) {
        const val = Number(v);

        // Always show min and max
        if (val === min) return min.toString();
        if (val === max) return max.toString();

        // Find closest sector
        let closest = null;
        let minDiff = Infinity;
        for (let i = 0; i < sectors.length; i++) {
          const diff = Math.abs(val - sectors[i]);
          if (diff < minDiff) {
            minDiff = diff;
            closest = sectors[i];
          }
        }

        // Only show if within tolerance
        const tolerance = (max - min) / 30;
        if (minDiff <= tolerance) return closest.toString();

        return '';
      }

      return {
        series: [
          {
            type: 'gauge',
            center: ['50%', '60%'],
            startAngle: 200,
            endAngle: -20,
            radius: '90%',
            min,
            max,
            splitNumber: 15,
            axisLine: {
              lineStyle: {
                width: 0,
                color: colorsBySectors
              }
            },
            pointer: { show: false },
            axisTick: {
              length: 2,
              distance: -10,
              lineStyle: { type: 'solid', color: 'auto', width: 15 }
            },
            progress: {
              show: true,
              overlap: false,
              roundCap: true,
              clip: false,
              distance: 0,
              itemStyle: { borderWidth: 12, borderColor: progressColor }
            },
            splitLine: { show: false },
            axisLabel: {
              color: '#fff',
              fontSize: 14,
              distance: -37.5,
              rotate: 'tangential',
              formatter: valuesBySectors
            },
            title: { offsetCenter: [0, '-45%'], fontSize: 18, color: '#CCC' },
            detail: {
              fontSize: 10,
              offsetCenter: [0, '40%'],
              valueAnimation: true,
              color: '#FFF',
              formatter: v => `{value|${v.toFixed((v<100)?1:0)}}\n\n{unit|bar}`,
              rich: {
                value: { fontSize: 30, fontWeight: 'bolder', color: '#FFF' },
                unit: { fontSize: 14, color: '#999', padding: [0, 0, 70, 0] }
              }
            },
            data: [{ value, name: 'Presión' }]
          }
        ]
      };
    },
    initChart(force = false) {
      if (force && this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      if (!this.chart) {
        this.chart = echarts.init(this.$refs[this.entityId]);
      }
      this.chart.setOption(this.buildOption(), true);
      this.handleResize();
    },
    getColorForValue(value) {
      for (let i = 0; i < this.sectors.length; i++) {
        const lower = i === 0 ? this.min : this.sectors[i - 1];
        const upper = this.sectors[i];
        if (value >= lower && value <= upper) return this.colors[i];
      }
      return this.colors[this.colors.length - 1];
    }
  }
};
</script>

<style scoped>
.gauge-container {
  display: inline-block;
  width: 220px;
  padding:0px;
  margin:0px;
}
.gauge {
  /* Ajusta el tamaño a tus necesidades */
  max-width: 210px;
  height: 175px;
  padding:0px;
  margin:0px;
}
</style>